import React from 'react';
import _ from 'lodash';
import { type User, UserAbilitiesEnum } from 'src/generated/graphql';

import type { IconName } from '@blueprintjs/core';
import type { Mutable } from '@pi/types';

export const routes = addDefaults({
    component: null,
    children: {
        search: {
            name: 'Search Data',
            icon: 'search',
            component: React.lazy(() => import('src/pages/UIv2/QueryPage')),
            children: {
                aggregate: {
                    name: 'Aggregate',
                    icon: 'function',
                    component: React.lazy(() => import('src/pages/UIv2/AggregatePage')),
                },
                data: {
                    name: 'Data Details',
                    icon: 'database',
                    component: React.lazy(() => import('src/pages/Data/pages/Edit')),
                    query: ['model', 'id'] as const,
                },
                history: {
                    name: 'Data History',
                    icon: 'history',
                    component: React.lazy(() => import('src/pages/Data/pages/History')),
                    query: ['model', 'id'] as const,
                    hideFromNav: true,
                },
            },
        },
        tags: {
            name: 'Tags',
            icon: 'tag',
            component: React.lazy(() => import('src/pages/Tags/pages/Overview')),
            query: ['model'] as const,
            children: {
                edit: {
                    name: 'Edit Tags',
                    icon: 'edit',
                    component: React.lazy(() => import('src/pages/Tags/pages/EditTags')),
                    query: ['model', 'mode', 'tag', 'query'] as const,
                },
            },
        },
        attributes: {
            name: 'Attributes',
            icon: 'layout-balloon',
            children: {
                stats: {
                    name: 'Attributes Definitions',
                    icon: 'grouped-bar-chart',
                    component: React.lazy(() => import('src/pages/Pv2/Attributes/pages/Stats')),
                },
                create: {
                    name: 'Create New Attribute Definition',
                    icon: 'add',
                    component: React.lazy(() => import('src/pages/Pv2/Attributes/pages/Create')),
                },
                view: {
                    hideFromNav: true,
                    name: 'View',
                    params: ['id'] as const,
                    component: React.lazy(() => import('src/pages/Pv2/Attributes/pages/View')),
                },
            },
            component: React.lazy(() => import('src/pages/Pv2/Attributes/index')),
        },
        ingredientManagement: {
            name: 'Ingredient Management',
            icon: 'shopping-cart',
            component: React.lazy(
                () => import('src/pages/IngredientManagement/pages/IngredientResearchPage'),
            ),
            isActive: user => user.isAdmin,
            children: {
                reviewedIngredients: {
                    name: 'Ingredient Research',
                    icon: 'eye-on',
                    component: React.lazy(
                        () => import('src/pages/IngredientManagement/pages/IngredientResearchPage'),
                    ),
                },
                singleIngredientReview: {
                    params: ['ingredientId'] as const,
                    name: 'Ingredient Review',
                    icon: 'shopping-cart',
                    hideFromNav: true,
                    component: React.lazy(
                        () =>
                            import(
                                'src/pages/IngredientManagement/pages/IngredientReviewStatusPage'
                            ),
                    ),
                },
                pv2sByIngredient: {
                    name: 'Pv2s By Ingredient',
                    icon: 'eye-on',
                    component: React.lazy(
                        () => import('src/pages/IngredientManagement/pages/Pv2sByIngredient'),
                    ),
                },
                newIngredientReview: {
                    name: 'New Ingredient Review',
                    icon: 'series-search',
                    hideFromNav: false,
                    isActive: user => user.isAdmin,
                    component: React.lazy(
                        () =>
                            import('src/pages/IngredientManagement/pages/NewIngredientReviewPage'),
                    ),
                },
            },
        },
        builder: {
            name: 'Builder',
            icon: 'build',
            component: React.lazy(() => import('src/pages/Builder/pages/FormulaList')),
            children: {
                batchCreate: {
                    name: 'Builder Batch',
                    icon: 'function',
                    component: React.lazy(() => import('src/pages/Builder/pages/BatchCreate')),
                    query: ['formula'] as const,
                },
                recompute: {
                    name: 'Dataflow Recompute',
                    icon: 'refresh',
                    component: React.lazy(() => import('src/pages/Builder/pages/RecomputeCreate')),
                },
                formulas: {
                    name: 'Formulas',
                    icon: 'search',
                    component: React.lazy(() => import('src/pages/Builder/pages/FormulaList')),
                },
                resources: {
                    name: 'Formula Resources',
                    icon: 'search',
                    component: React.lazy(
                        () => import('src/pages/Builder/pages/FormulaResourceList'),
                    ),
                },
                formulaNew: {
                    name: 'Create New Formula',
                    icon: 'add-to-artifact',
                    component: React.lazy(() => import('src/pages/Builder/pages/FormulaCreate')),
                },
                batchView: {
                    name: 'Batch View',
                    hideFromNav: true,
                    params: ['batchId'] as const,
                    component: React.lazy(() => import('src/pages/Builder/pages/BatchView')),
                },
                formulaEdit: {
                    name: 'Formula Edit',
                    hideFromNav: true,
                    params: ['formulaId'] as const,
                    component: React.lazy(() => import('src/pages/Builder/pages/FormulaEdit')),
                },
                resourceEdit: {
                    name: 'Formula Resource Edit',
                    hideFromNav: true,
                    params: ['formulaResourceId'] as const,
                    component: React.lazy(
                        () => import('src/pages/Builder/pages/FormulaResourceEdit'),
                    ),
                },
                resourceNew: {
                    name: 'Resource Create',
                    hideFromNav: true,
                    component: React.lazy(
                        () => import('src/pages/Builder/pages/FormulaResourceEdit'),
                    ),
                },
            },
        },
        config: {
            name: 'Configurations',
            icon: 'code-block',
            component: React.lazy(() => import('src/pages/Configurations')),
        },
        transformer: {
            name: 'Transformer',
            icon: 'playbook',
            component: React.lazy(() => import('src/pages/Transformer/pages/List')),
            children: {
                edit: {
                    params: ['expressionId'] as const,
                    query: ['taskDependencies', 'task'] as const,
                    name: 'Edit',
                    icon: 'edit',
                    hideFromNav: true,
                    component: React.lazy(() => import('src/pages/Transformer/pages/Edit')),
                },
                new: {
                    name: 'Create',
                    icon: 'add-to-artifact',
                    hideFromNav: true,
                    component: React.lazy(() => import('src/pages/Transformer/pages/Create')),
                },
                resources: {
                    name: 'Transformation Resources',
                    icon: 'search',
                    hideFromNav: false,
                    component: React.lazy(() => import('src/pages/Transformer/pages/Resources')),
                },
            },
        },
        tasker: {
            name: 'Tasker',
            icon: 'flow-branch',
            component: React.lazy(() => import('src/pages/Tasker')),
            children: {
                task: {
                    icon: 'new-grid-item',
                    name: 'Tasks',
                    component: React.lazy(() => import('src/pages/Tasks/pages/List')),
                    children: {
                        queue: {
                            name: 'Task Queue',
                            icon: 'sort-asc',
                            component: React.lazy(() => import('src/pages/Tasks/pages/Queue')),
                            isActive: user => user.isAdmin || !!user.tasker?.enabled,
                            children: {
                                view: {
                                    name: 'Handle Task',
                                    icon: 'blank',
                                    params: ['taskId'] as const,
                                    hideFromNav: true,
                                    component: React.lazy(
                                        () => import('src/pages/Tasks/pages/ProcessFormTask'),
                                    ),
                                },
                            },
                        },
                        list: {
                            name: 'SearchV1 Tasks',
                            icon: 'search',
                            component: React.lazy(() => import('src/pages/Tasks/pages/List')),
                        },
                        new: {
                            name: 'Create New Task',
                            icon: 'add-to-artifact',
                            component: React.lazy(() => import('src/pages/Tasks/pages/Create')),
                        },
                        newWithId: {
                            name: 'Create',
                            icon: 'add-to-artifact',
                            params: ['taskDefinitionId'] as const,
                            hideFromNav: true,
                            component: React.lazy(() => import('src/pages/Tasks/pages/Create')),
                        },
                        view: {
                            hideFromNav: true,
                            name: 'View',
                            params: ['taskId'] as const,
                            component: React.lazy(() => import('src/pages/Tasks/pages/View')),
                        },
                    },
                },
                taskDefinition: {
                    icon: 'layout-hierarchy',
                    name: 'Task Definition',
                    component: React.lazy(() => import('src/pages/TaskDefinition/pages/List')),
                    children: {
                        list: {
                            name: 'SearchV1 Task Definitions',
                            icon: 'search',
                            component: React.lazy(
                                () => import('src/pages/TaskDefinition/pages/List'),
                            ),
                        },
                        new: {
                            name: 'Create New Task Definition',
                            icon: 'add-to-artifact',
                            component: React.lazy(
                                () => import('src/pages/TaskDefinition/pages/Create'),
                            ),
                        },
                        edit: {
                            name: 'Edit',
                            icon: 'edit',
                            params: ['taskId'] as const,
                            component: React.lazy(
                                () => import('src/pages/TaskDefinition/pages/Edit'),
                            ),
                            hideFromNav: true,
                        },
                    },
                },
                form: {
                    name: 'Form Builder',
                    icon: 'form',
                    component: React.lazy(() => import('src/pages/FormBuilder/pages/List')),
                    children: {
                        list: {
                            name: 'SearchV1 Task Forms',
                            icon: 'search',
                            component: React.lazy(() => import('src/pages/FormBuilder/pages/List')),
                        },
                        new: {
                            name: 'Create New Task Form',
                            icon: 'new-layer',
                            component: React.lazy(
                                () => import('src/pages/FormBuilder/pages/Create'),
                            ),
                        },
                        newFrom: {
                            name: 'New From',
                            component: React.lazy(
                                () => import('src/pages/FormBuilder/pages/Create'),
                            ),
                            params: ['formId'] as const,
                            hideFromNav: true,
                        },
                        edit: {
                            name: 'Edit',
                            icon: 'edit',
                            params: ['formId'] as const,
                            hideFromNav: true,
                            component: React.lazy(() => import('src/pages/FormBuilder/pages/Edit')),
                        },
                        mturkPreviewViewer: {
                            name: 'Preview Task Form',
                            icon: 'panel-stats',
                            component: React.lazy(() => import('src/pages/Mturk/MturkTaskPreview')),
                        },
                        instructions: {
                            name: 'Manage Task Form Instructions',
                            icon: 'info-sign',
                            query: ['formId'],
                            component: React.lazy(
                                () => import('src/pages/FormBuilder/pages/Instructions'),
                            ),
                        },
                        instructionsEdit: {
                            name: 'Instructions',
                            icon: 'info-sign',
                            hideFromNav: true,
                            params: ['formInstructionId'] as const,
                            query: ['formId'],
                            component: React.lazy(
                                () => import('src/pages/FormBuilder/pages/Instructions'),
                            ),
                        },
                        mturkPreview: {
                            query: ['taskId'] as const,
                            name: 'Preview',
                            hideFromNav: true,
                            component: React.lazy(() => import('src/pages/Mturk/AppMturk')),
                        },
                    },
                },
                productHistory: {
                    name: 'Product Ingestion History',
                    icon: 'history',
                    component: React.lazy(() => import('src/pages/Tasks/pages/ProductHistory')),
                },
            },
        },
        imageProcessing: {
            name: 'Image Processing',
            icon: 'media',
            component: React.lazy(() => import('src/pages/ImageProcessing/pages/DownloadImages')),
            isActive: user =>
                user.isAdmin || user.abilities?.includes(UserAbilitiesEnum.ImageProcessing),
            children: {
                manage: {
                    name: 'Manage Images',
                    icon: 'edit',
                    hideFromNav: false,
                    isActive: user => user.isAdmin,
                    component: React.lazy(
                        () => import('src/pages/ImageProcessing/pages/ManageImages'),
                    ),
                },
                download: {
                    name: 'Download Images',
                    icon: 'download',
                    hideFromNav: false,
                    isActive: user =>
                        user.isAdmin || user.abilities?.includes(UserAbilitiesEnum.ImageProcessing),
                    component: React.lazy(
                        () => import('src/pages/ImageProcessing/pages/DownloadImages'),
                    ),
                },
                upload: {
                    name: 'Upload Images',
                    icon: 'upload',
                    hideFromNav: false,
                    isActive: user =>
                        user.isAdmin || user.abilities?.includes(UserAbilitiesEnum.ImageProcessing),
                    component: React.lazy(
                        () => import('src/pages/ImageProcessing/pages/UploadImages'),
                    ),
                },
            },
        },
        curvball: {
            name: 'Curvball',
            icon: 'th-list',
            // TODO: Address this in a future step, as it is not gated for Step 1.
            isActive: () => true,
            component: React.lazy(() => import('src/pages/Curvball')),
            children: {
                loadSource: {
                    name: 'Load Source',
                    icon: 'import',
                    // The parent is deliberately ungated, but this page drives RunScript, which is
                    // admin-only, so don't offer it to users who cannot use it.
                    isActive: user => user.isAdmin,
                    component: React.lazy(() => import('src/pages/Curvball/LoadSource')),
                },
            },
        },
        utilities: {
            name: 'Utilities',
            icon: 'cog',
            isActive: user => user.isAdmin,
            component: React.lazy(() => import('src/pages/Utilities')),
            children: {
                scripts: {
                    name: 'Run Scripts',
                    icon: 'document',
                    hideFromNav: false,
                    component: React.lazy(() => import('src/pages/Scripts/pages/ScriptsPage')),
                    children: {
                        run: {
                            params: ['key'] as const,
                            name: 'run',
                            hideFromNav: true,
                            component: React.lazy(
                                () => import('src/pages/Scripts/pages/ScriptPage'),
                            ),
                        },
                        runs: {
                            params: ['scriptKey'] as const,
                            name: 'logs',
                            hideFromNav: true,
                            component: React.lazy(
                                () => import('src/pages/Scripts/pages/ScriptRuns'),
                            ),
                        },
                    },
                },
                bulkUpdate: {
                    name: 'Bulk Update',
                    icon: 'upload',
                    component: React.lazy(() => import('src/pages/BulkUpdate')),
                },
                circana: {
                    name: 'Circana Management',
                    icon: 'circle',
                    isActive: user => user.abilities?.includes(UserAbilitiesEnum.CircanaManager),
                    component: React.lazy(() => import('src/pages/CircanaManagement')),
                },
                services: {
                    name: 'Restart Services',
                    icon: 'antenna',
                    component: React.lazy(() => import('src/pages/Services')),
                },
                users: {
                    name: 'Users',
                    icon: 'user',
                    component: React.lazy(() => import('src/pages/Users/pages/List')),
                    children: {
                        edit: {
                            params: ['userId'] as const,
                            name: 'Edit',
                            icon: 'edit',
                            hideFromNav: true,
                            component: React.lazy(() => import('src/pages/Users/pages/Edit')),
                        },
                        list: {
                            name: 'Manage Users',
                            icon: 'user',
                            component: React.lazy(() => import('src/pages/Users/pages/List')),
                        },
                        mturk: {
                            name: 'Manage MTurk Users',
                            icon: 'person',
                            component: React.lazy(
                                () => import('src/pages/Users/pages/MturkWorkerList'),
                            ),
                        },
                        mturkEdit: {
                            params: ['mturkWorkerId'] as const,
                            name: 'Edit',
                            icon: 'edit',
                            hideFromNav: true,
                            component: React.lazy(
                                () => import('src/pages/Users/pages/MturkWorkerEdit'),
                            ),
                        },
                    },
                },
                featureFlags: {
                    name: 'Feature Flags',
                    icon: 'flag',
                    component: React.lazy(() => import('src/pages/FeatureFlags')),
                    isActive: user =>
                        user.isAdmin &&
                        user.abilities?.includes(UserAbilitiesEnum.ConfigureFeatureFlags),
                },
                costAndBilling: {
                    name: 'Cost and Billing',
                    icon: 'dollar',
                    isActive: user =>
                        user.isAdmin && user.abilities?.includes(UserAbilitiesEnum.CostAndBilling),
                    component: React.lazy(() => import('src/pages/Utilities/CostAndBillingPage')),
                },
            },
        },
        hierarchyManagement: {
            name: 'Hierarchy Management',
            icon: 'diagram-tree',
            component: React.lazy(
                () => import('src/pages/HierarchyManagement/HierarchyManagement'),
            ),
            children: {
                taxonomy: {
                    name: 'Taxonomy',
                    icon: 'gantt-chart',
                    component: React.lazy(
                        () => import('src/pages/HierarchyManagement/pages/Taxonomy'),
                    ),
                },
                hierarchyMapping: {
                    name: 'Hierarchy Mapping',
                    icon: 'flows',
                    component: React.lazy(
                        () => import('src/pages/HierarchyManagement/pages/HierarchyMapping/List'),
                    ),
                    children: {
                        list: {
                            name: 'Mapping List',
                            icon: 'list',
                            component: React.lazy(
                                () =>
                                    import(
                                        'src/pages/HierarchyManagement/pages/HierarchyMapping/List'
                                    ),
                            ),
                        },
                        create: {
                            name: 'Create Mapping',
                            icon: 'add',
                            component: React.lazy(
                                () =>
                                    import(
                                        'src/pages/HierarchyManagement/pages/HierarchyMapping/pages/Create'
                                    ),
                            ),
                        },
                        edit: {
                            name: 'Edit Mapping',
                            icon: 'edit',
                            params: ['id'] as const,
                            query: ['pv2Id'] as const,
                            hideFromNav: true,
                            component: React.lazy(
                                () =>
                                    import(
                                        'src/pages/HierarchyManagement/pages/HierarchyMapping/pages/Edit'
                                    ),
                            ),
                        },
                        bulkRunner: {
                            name: 'Bulk Runner',
                            icon: 'double-chevron-right',
                            query: ['keycat'] as const,
                            component: React.lazy(
                                () =>
                                    import(
                                        'src/pages/HierarchyManagement/pages/HierarchyMapping/pages/BulkRunner/BulkRunner'
                                    ),
                            ),
                        },
                    },
                },
            },
        },
        clientTrackerPage: {
            name: 'Client Tracker',
            icon: 'database',
            component: React.lazy(() => import('src/pages/ClientTracker/Search')),
            children: {
                edit: {
                    name: 'Edit Client Tracker',
                    icon: 'upload',
                    component: React.lazy(() => import('src/pages/ClientTracker/Edit')),
                },
                update: {
                    name: 'BulkUpdate Client Tracker',
                    icon: 'edit',
                    hideFromNav: true,
                    component: React.lazy(() => import('src/pages/BulkUpdate')),
                },
            },
        },
        dataManagement: {
            name: 'Data Management',
            icon: 'data-lineage',
            component: React.lazy(() => import('src/pages/DataManagement/PredictionOverride')),
            children: {
                predictionOverride: {
                    name: 'Prediction Override',
                    icon: 'predictive-analysis',
                    component: React.lazy(
                        () => import('src/pages/DataManagement/PredictionOverride'),
                    ),
                    children: {
                        bulk: {
                            name: 'Bulk Prediction Override',
                            icon: 'upload',
                            component: React.lazy(
                                () =>
                                    import('src/pages/DataManagement/PredictionOverrideBulkUpload'),
                            ),
                        },
                    },
                },
                brandOutlierDetection: {
                    name: 'Brand Outlier Detection',
                    icon: 'comparison',
                    component: React.lazy(
                        () => import('src/pages/DataManagement/BrandOutlierDetection'),
                    ),
                },
            },
        },
        reportingDashboard: {
            name: 'Reporting Dashboard',
            icon: 'dashboard',
            component: React.lazy(() => import('src/pages/ReportingDashboard/ReportingDashboard')),
            children: {
                mismatches: {
                    hideFromNav: true,
                    name: 'Mismatches Report',
                    icon: 'th-list',
                    component: React.lazy(
                        () => import('src/pages/ReportingDashboard/pages/MismatchesReport'),
                    ),
                },
                incorrectAttributeName: {
                    hideFromNav: true,
                    name: 'Incorrect Attribute Name Report',
                    icon: 'th-list',
                    component: React.lazy(
                        () =>
                            import(
                                'src/pages/ReportingDashboard/pages/IncorrectAttributeNameReport'
                            ),
                    ),
                },
                attributeSalesAssessment: {
                    name: 'R&D Attribute Sales Assessment',
                    icon: 'th-list',
                    hideFromNav: true,
                    query: ['builderBatchId', 'runReportId'] as const,
                    component: React.lazy(
                        () =>
                            import(
                                'src/pages/ReportingDashboard/pages/AttributeSalesAssessmentReport'
                            ),
                    ),
                },
                attributeSalesAssessmentRun: {
                    name: 'Attribute Sales Assessment Analysis Run',
                    icon: 'play',
                    hideFromNav: true,
                    component: React.lazy(
                        () =>
                            import(
                                'src/pages/ReportingDashboard/pages/AttributeSalesAssessmentAnalysisRun'
                            ),
                    ),
                },
                coverageReport: {
                    name: 'Coverage Report',
                    icon: 'chart',
                    hideFromNav: true,
                    component: React.lazy(
                        () =>
                            import(
                                'src/pages/ReportingDashboard/pages/CoverageReport/CoverageReport'
                            ),
                    ),
                },
                itemActivation: {
                    name: 'Item Activation & Attribute Change Report',
                    icon: 'th-list',
                    hideFromNav: true,
                    component: React.lazy(
                        () => import('src/pages/ReportingDashboard/pages/ItemActivation'),
                    ),
                },
                clientFeedback: {
                    name: 'Client Feedback Report',
                    icon: 'th-list',
                    hideFromNav: true,
                    component: React.lazy(
                        () => import('src/pages/ReportingDashboard/pages/ClientFeedbackReport'),
                    ),
                },
                qcSelfServe: {
                    name: 'QC Self-Serve Report',
                    icon: 'chart',
                    hideFromNav: true,
                    component: React.lazy(
                        () => import('src/pages/ReportingDashboard/pages/QCSelfServeReport'),
                    ),
                },
            },
        },
        v1: {
            name: 'Old Search',
            hideFromNav: true,
            component: React.lazy(() => import('src/pages/Search/Search')),
            query: ['query'] as const,
            params: ['collection'] as const,
        },
    },
});

export default routes;

function addDefaults<Root extends RouteDefinitionInput>(root: Root): MakeRoute<Root> {
    const defaultIsActive = (user: User) => !!user.isAdmin;

    const it = (route: Mutable<RouteDefinitionInput>, isActive: (user: User) => boolean): any => {
        let routeIsActive = route.isActive || isActive;
        let childIsActive = route.isActive || routeIsActive;

        if (route === root) {
            routeIsActive = () => true;
            childIsActive = defaultIsActive;
        }

        return {
            ...route,
            isActive: routeIsActive,
            children: route.children
                ? _.mapValues(route.children, child => it(child, childIsActive))
                : undefined,
        };
    };

    return it(root, defaultIsActive);
}

type MakeRoute<R extends RouteDefinitionInput> = R & {
    isActive: RouteDefinition['isActive'];
    children: R['children'] extends Record<string, RouteDefinitionInput>
        ? {
              [K in keyof R['children']]: MakeRoute<R['children'][K]>;
          }
        : undefined;
};

export interface RouteDefinition extends RouteDefinitionInput {
    readonly isActive: NonNullable<RouteDefinitionInput['isActive']>;
    readonly children?: Record<string, RouteDefinition>;
}

interface RouteDefinitionInput {
    readonly icon?: IconName;
    readonly name?: string;
    readonly component: React.LazyExoticComponent<any> | null;
    readonly hideFromNav?: boolean;
    readonly group?: string;
    readonly params?: readonly string[];
    readonly query?: readonly string[];
    readonly children?: Record<string, RouteDefinitionInput>;
    /**
     * by default, every route will only be available to internal admin users
     *
     * is a route has children, they will inherit the parent's permissions unless overwritten
     */
    readonly isActive?: (user: User) => boolean;
}
